import { useCallback, useEffect, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublishIcon from "@mui/icons-material/Publish";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import { toast } from "react-toastify";
import Listtable from "components/common/ListTable";
import { listSpecialities } from "api/patientHistoryMaster";
import {
  deleteAdvisoryTemplate,
  downloadAdvisoryTemplateFile,
  getAdvisoryTemplate,
  getAdvisoryTemplates,
  importAdvisoryTemplates,
  setAdvisoryTemplateStatus,
} from "api/advisoryTemplate";
import AdvisoryTemplateDialog from "./AdvisoryTemplateDialog";
import {
  TEMPLATE_STATUS,
  TEMPLATE_STATUS_OPTIONS,
  contentPreview,
  importFailures,
  importSummary,
  statusLabel,
  templateStatusOf,
} from "./advisoryTemplateForm";

export const TABLE_HEADERS = [
  "Advisory Name",
  "Speciality",
  "Status",
  "Advisory Content",
  "Updated",
  "Action",
];

const TABLE_KEYS = [
  "name",
  "specialityLabel",
  "statusCell",
  "contentCell",
  "updatedLabel",
  "action",
];

const btnStyle = {
  width: "max-content",
  padding: "0.5rem 2rem",
  color: "#194AF5",
  bgcolor: "#E6EBFF",
  boxShadow: "none",
  borderRadius: "8px",
  ":hover": { bgcolor: "#E6EBFF", color: "#194AF5", boxShadow: "none" },
};

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * DRCFO Admin → Templates → Advisory Templates. Lists, filters, adds, edits,
 * publishes / drafts and deletes the advisories DRCFO manages per speciality,
 * with Download Template / Import / Export over the same sheet.
 * `editId` (from the speciality cards' list) opens that advisory for editing.
 */
const AdvisoryTemplateMaster = ({ editId = "", onEditHandled }) => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [specialityOptions, setSpecialityOptions] = useState([]);
  const fileInputRef = useRef(null);

  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [specialityFilter, setSpecialityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
      setPageNumber(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    listSpecialities()
      .then((list) =>
        setSpecialityOptions(
          list.slice().sort((a, b) => String(a?.name).localeCompare(String(b?.name)))
        )
      )
      .catch(() => toast.error("Could not load specialities"));
  }, []);

  const filterParams = useCallback(
    () => ({
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(specialityFilter ? { speciality: specialityFilter } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    [debouncedSearch, specialityFilter, statusFilter]
  );

  const openEditor = useCallback((template) => {
    setActiveTemplate(template || {});
    setModalOpen(true);
  }, []);

  const toggleStatus = async (item) => {
    const next =
      templateStatusOf(item) === TEMPLATE_STATUS.PUBLISHED
        ? TEMPLATE_STATUS.DRAFT
        : TEMPLATE_STATUS.PUBLISHED;
    setBusy(true);
    try {
      const response = await setAdvisoryTemplateStatus(item._id, next);
      toast.success(
        response?.data?.message ||
          (next === TEMPLATE_STATUS.PUBLISHED
            ? "Advisory template published"
            : "Advisory template moved to draft")
      );
      setRows((list) =>
        list.map((row) => (row._id === item._id ? { ...row, templateStatus: next } : row))
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not update the status.");
    } finally {
      setBusy(false);
    }
  };

  // The table cells are derived at render from the raw rows the API answered.
  const decorate = (item) => {
    const status = templateStatusOf(item);
    const published = status === TEMPLATE_STATUS.PUBLISHED;
    return {
      ...item,
      statusCell: (
        <Chip
          size="small"
          label={statusLabel(status)}
          color={published ? "success" : "warning"}
          variant="outlined"
        />
      ),
      contentCell: (
        <Tooltip
          title={<Box sx={{ whiteSpace: "pre-line" }}>{item.generalAdvice}</Box>}
          placement="top-start"
        >
          <Box sx={{ maxWidth: 420, color: "#555" }}>{contentPreview(item.generalAdvice)}</Box>
        </Tooltip>
      ),
      updatedLabel: formatDate(item.updatedAt),
      action: (
        <Box sx={{ display: "flex", gap: "0.25rem" }}>
          <IconButton aria-label={`Edit ${item.name}`} onClick={() => openEditor(item)}>
            <EditIcon fontSize="small" color="primary" />
          </IconButton>
          <IconButton
            aria-label={`${published ? "Move to draft" : "Publish"} ${item.name}`}
            onClick={() => toggleStatus(item)}
          >
            {published ? (
              <UnpublishedIcon fontSize="small" color="warning" />
            ) : (
              <PublishIcon fontSize="small" color="success" />
            )}
          </IconButton>
          <IconButton aria-label={`Delete ${item.name}`} onClick={() => setDeleteTarget(item)}>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </IconButton>
        </Box>
      ),
    };
  };

  const fetchTemplates = useCallback(() => {
    setLoading(true);
    getAdvisoryTemplates({ page: pageNumber + 1, perPage: rowsPerPage, ...filterParams() })
      .then((response) => {
        const payload = response?.data || {};
        const list = Array.isArray(payload.data) ? payload.data : [];
        setRows(list);
        const total = Number(payload?.meta?.total);
        setCount(Number.isFinite(total) ? total : list.length);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Could not load advisory templates.");
      })
      .finally(() => setLoading(false));
  }, [pageNumber, rowsPerPage, filterParams]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Opened from a speciality card's list: load that advisory and edit it.
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    getAdvisoryTemplate(editId)
      .then((response) => {
        if (cancelled) return;
        const template = response?.data?.data;
        if (template?._id) openEditor(template);
        else toast.error("Advisory template not found");
      })
      .catch((err) => {
        if (!cancelled) toast.error(err?.response?.data?.message || "Advisory template not found");
      })
      .finally(() => {
        if (!cancelled && typeof onEditHandled === "function") onEditHandled();
      });
    return () => {
      cancelled = true;
    };
  }, [editId, openEditor, onEditHandled]);

  const handleModalClose = (reason = "save") => {
    setModalOpen(false);
    setActiveTemplate({});
    if (reason !== "close") fetchTemplates();
  };

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    setBusy(true);
    try {
      await deleteAdvisoryTemplate(deleteTarget._id);
      toast.success("Advisory template deleted successfully");
      if (rows.length === 1 && pageNumber > 0) setPageNumber(pageNumber - 1);
      else fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not delete this advisory template.");
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  const handleDownload = async (kind, filename) => {
    setBusy(true);
    try {
      const response = await downloadAdvisoryTemplateFile(kind, kind === "export" ? filterParams() : {});
      saveBlob(response.data, filename);
    } catch (err) {
      toast.error("Download failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = null;
    if (!file) return;

    setBusy(true);
    try {
      const response = await importAdvisoryTemplates(file);
      const summary = importSummary(response?.data);
      const failures = importFailures(response?.data);
      // Failed rows need their reasons, which a toast cannot hold.
      if (failures.length > 0) setImportResult({ message: summary.message, failures });
      else if (summary.level === "warning") toast.warning(summary.message);
      else toast.success(summary.message);
      setPageNumber(0);
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "1rem 0", flexWrap: "wrap" }}
      >
        <TextField
          size="small"
          label="Search advisories"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          sx={{ minWidth: 240 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="advisory-speciality-filter-label">Speciality</InputLabel>
          <Select
            labelId="advisory-speciality-filter-label"
            label="Speciality"
            value={specialityFilter}
            onChange={(event) => {
              setSpecialityFilter(event.target.value);
              setPageNumber(0);
            }}
            inputProps={{ "data-testid": "advisory-speciality-filter" }}
          >
            <MenuItem value="">All</MenuItem>
            {specialityOptions.map((option) => (
              <MenuItem key={option._id} value={option._id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140, mr: "auto" }}>
          <InputLabel id="advisory-status-filter-label">Status</InputLabel>
          <Select
            labelId="advisory-status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPageNumber(0);
            }}
            inputProps={{ "data-testid": "advisory-status-filter" }}
          >
            {TEMPLATE_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value || "all"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("template", "advisory_templates_template.xlsx")}
        >
          Download Template
        </Button>

        <Button sx={btnStyle} variant="contained" disabled={busy} onClick={() => fileInputRef.current?.click()}>
          Import
        </Button>
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          ref={fileInputRef}
          hidden
          data-testid="advisory-template-import-input"
          onChange={handleImport}
        />

        <Button
          sx={btnStyle}
          variant="contained"
          disabled={busy}
          onClick={() => handleDownload("export", "advisory_templates_export.xlsx")}
        >
          Export
        </Button>

        <Button
          sx={{ ...btnStyle, bgcolor: "#194AF5", color: "white", ":hover": {} }}
          variant="contained"
          disabled={busy}
          onClick={() => openEditor({})}
        >
          Add Advisory Template
        </Button>
      </Box>

      <Listtable
        tableheader={TABLE_HEADERS}
        tablebody={rows.map(decorate)}
        rowsPerPage={rowsPerPage}
        page={pageNumber}
        handleChangePage={(event, newPage) => setPageNumber(newPage)}
        handleChangeRowsPerPage={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPageNumber(0);
        }}
        totalCount={count}
        dataKeyName={TABLE_KEYS}
        tag="advisoryTemplates"
      />

      {modalOpen && (
        <AdvisoryTemplateDialog
          open={modalOpen}
          onClose={handleModalClose}
          data={activeTemplate}
          specialityOptions={specialityOptions}
        />
      )}

      <Dialog open={Boolean(importResult)} onClose={() => setImportResult(null)} fullWidth maxWidth="sm">
        <DialogTitle>Import complete</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>{importResult?.message}</DialogContentText>
          <Box sx={{ fontWeight: 600, mb: 1 }}>Failed rows:</Box>
          <Box
            component="ul"
            aria-label="Failed rows"
            sx={{ listStyle: "none", p: 0, m: 0, maxHeight: 320, overflowY: "auto" }}
          >
            {(importResult?.failures ?? []).map((item, index) => (
              <Box
                component="li"
                key={`${item.row}-${item.label}-${index}`}
                sx={{ py: 1, borderBottom: "1px solid #EEE", fontSize: "0.875rem" }}
              >
                <Box sx={{ fontWeight: 600 }}>
                  {item.row ? `Row ${item.row}` : "Row"}
                  {item.label ? ` — ${item.label}` : ""}
                </Box>
                <Box sx={{ color: "#d32f2f" }}>Reason: {item.reason}</Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete advisory template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteTarget?.name}"
            {deleteTarget?.specialityLabel ? ` (${deleteTarget.specialityLabel})` : ""}? It will stop
            appearing in every doctor's Templates Master. Prescriptions that already applied it are
            not changed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={busy}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop open={loading || busy} sx={{ zIndex: 1200, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default AdvisoryTemplateMaster;
